import React, { lazy, Suspense } from "react";
import tw from "twin.macro";
import { useTranslation } from "react-i18next";

import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Header from "components/headers/light.js";
import Hero from "components/hero/TwoColumnWithVideo.js";

import chefIconImageSrc from "images/chef-icon.svg";
import celebrationIconImageSrc from "images/celebration-icon.svg";
import shopIconImageSrc from "images/shop-icon.svg";
import CarneAsada from "../images/food/lechonAsado.png";
import TruckRestaurant from "../images/restaurant/ElPilonTruck.png";

// Lazy-load below-fold sections to reduce initial JS parse time
const Features = lazy(() => import("components/features/ThreeColSimple.js"));
const MainFeature = lazy(() => import("components/features/TwoColWithButton.js"));
const MainFeature2 = lazy(() => import("components/features/TwoColSingleFeatureWithStats2.js"));
const TabGrid = lazy(() => import("components/cards/TabCardGrid.js"));
const Testimonial = lazy(() => import("components/testimonials/ThreeColumnWithProfileImage.js"));
const DownloadApp = lazy(() => import("components/cta/DownloadApp.js"));
const Footer = lazy(() => import("components/footers/FiveColumnWithInputForm.js"));

const RestaurantBackground = process.env.PUBLIC_URL + "/hero.webp";

export default () => {
  const { t } = useTranslation();
  const Subheading = tw.span`tracking-wider text-sm font-medium`;
  const HighlightedText = tw.span`bg-primary-500 text-gray-100 px-4 transform -skew-x-12 inline-block`;
  const HighlightedTextInverse = tw.span`bg-gray-100 text-primary-500 px-4 transform -skew-x-12 inline-block`;
  const Description = tw.span`inline-block mt-8`;
  const imageCss = tw`rounded-4xl`;

  return (
    <>
    <Header />
    <AnimationRevealPage>
      <Hero
        heading={<>{t("hero.heading")} <HighlightedText>{t("hero.headingHighlight")}</HighlightedText></>}
        description={t("hero.description")}
        imageSrc={RestaurantBackground}
        imageCss={imageCss}
        imageDecoratorBlob={true}
        primaryButtonText={t("hero.orderBtn")}
        watchVideoButtonText={t("hero.videoBtn")}
      />
      <Suspense fallback={null}>
      <section id="about" aria-label="About us">
      <MainFeature
        subheading={<Subheading>{t("about.subheading")}</Subheading>}
        heading={
          <>
            {t("about.heading")}
            <wbr /> <HighlightedText>{t("about.headingHighlight")}</HighlightedText>
          </>
        }
        description={
          <Description>
            {t("about.description1")}
            <br />
            <br />
            {t("about.description2")}
          </Description>
        }
        buttonRounded={false}
        textOnLeft={false}
        primaryButtonText={t("about.orderBtn")}
        primaryButtonUrl="/viewRestaurant"
        imageSrc={CarneAsada}
        imageCss={imageCss}
        imageDecoratorBlob={true}
        imageDecoratorBlobCss={tw`left-1/2 -translate-x-1/2 md:w-32 md:h-32 opacity-25`}
      />
      </section>
      <section id="menu" aria-label="Menu">
      <TabGrid
        heading={
          <>
            {t("menu.heading")} <HighlightedText>{t("menu.headingHighlight")}</HighlightedText>
          </>
        }
      />
      </section>
      <Features
        heading={
          <>
            {t("services.heading")} <HighlightedText>{t("services.headingHighlight")}</HighlightedText>
          </>
        }
        cards={[
          {
            imageSrc: shopIconImageSrc,
            title: t("services.card1.title"),
            description: t("services.card1.description"),
            url: "tel:+18643860676"
          },
          {
            imageSrc: celebrationIconImageSrc,
            title: t("services.card2.title"),
            description: t("services.card2.description"),
            url: "tel:+18643860676"
          },
          {
            imageSrc: chefIconImageSrc,
            title: t("services.card3.title"),
            description: t("services.card3.description"),
            url: "tel:+18643860676"
          }
        ]}
        imageContainerCss={tw`p-2!`}
        imageCss={tw`w-20! h-20!`}
      />
      <MainFeature2
        subheading={<Subheading>{t("whyUs.subheading")}</Subheading>}
        heading={<>{t("whyUs.heading")} <HighlightedText>{t("whyUs.headingHighlight")}</HighlightedText></>}
        primaryButtonText={t("whyUs.orderBtn")}
        primaryButtonUrl=""
        imageInsideDiv={false}
        imageSrc={TruckRestaurant}
        imageCss={Object.assign(tw`bg-cover`, imageCss)}
        imageContainerCss={tw`md:w-1/2 h-auto`}
        imageDecoratorBlob={true}
        imageDecoratorBlobCss={tw`left-1/2 md:w-32 md:h-32 -translate-x-1/2 opacity-25`}
        textOnLeft={true}
      />
      <Testimonial
        subheading=""
        heading={<>{t("testimonials.heading")} <HighlightedText>{t("testimonials.headingHighlight")}</HighlightedText></>}
      />
      <DownloadApp
        text={<>{t("doordash.text")} <HighlightedTextInverse style={{ color: "red" }}>{t("doordash.highlight")}</HighlightedTextInverse></>}
      />
      <section id="contact" aria-label="Contact">
        <Footer />
      </section>
      </Suspense>
    </AnimationRevealPage>
    </>
  );
}
